import { h, Component } from 'preact';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';

import Camera from '../gui/camera';
import { decodeQrFromImageFile } from '../../lib/qrDecode';

function toDateInput(value) {
    if (!value) {
        return '';
    }
    const text = String(value);
    return /^\d{4}-\d{2}-\d{2}/.test(text) ? text.slice(0, 10) : '';
}

function initialState({ isNew, card }) {
    const c = (!isNew && card) || {};
    return {
        qrPayload: c.qrPayload || '',
        brand: c.brand || '',
        model: c.model || '',
        caliber: c.caliber || '',
        validUntil: toDateInput(c.validUntil),
        note: c.note || '',
        scanning: false,
        decoding: false,
        decodeFailed: false,
        error: '',
        isSaving: false,
    };
}

@observer
class WeaponCardDrawer extends Component {
    constructor(props) {
        super(props);
        this.state = initialState(props);
    }

    onField = (e) => {
        const { name, value } = e.target;
        this.setState({ [name]: value, error: '' });
    }

    startScan = () => {
        this.setState({ scanning: true, decodeFailed: false });
    }

    stopScan = () => {
        this.setState({ scanning: false });
    }

    onScan = ({ data } = {}) => {
        const text = String(data || '').trim();
        if (!text) {
            return;
        }
        this.setState({ qrPayload: text, scanning: false, decodeFailed: false, error: '' });
    }

    onFile = async (e) => {
        const file = e.target.files && e.target.files[0];
        if (!file) {
            return;
        }
        this.setState({ decoding: true, decodeFailed: false });
        let text = null;
        try {
            text = await decodeQrFromImageFile(file);
        } catch (err) {
            text = null;
        }
        if (text) {
            this.setState({ qrPayload: text, decoding: false, decodeFailed: false, error: '' });
        } else {
            this.setState({ decoding: false, decodeFailed: true });
        }
        e.target.value = '';
    }

    canSave = () => {
        const { qrPayload, isSaving } = this.state;
        return !isSaving && qrPayload.trim().length > 0;
    }

    save = async () => {
        const { weaponCardStore, appState } = this.props.stores;
        const {
            isNew, card, callback = () => {}, drawerLevel = 1,
        } = this.props;
        const {
            qrPayload, brand, model, caliber, validUntil, note,
        } = this.state;
        if (!this.canSave()) {
            return;
        }
        this.setState({ isSaving: true, error: '' });
        const input = {
            qrPayload: qrPayload.trim(), brand, model, caliber, validUntil, note,
        };
        const response = isNew
            ? await weaponCardStore.addCard(input)
            : await weaponCardStore.updateCard(card.uuidv4, input);
        if (response?.status === 201 || response?.status === 202) {
            callback();
            appState.toggleDrawer(false, drawerLevel);
            return;
        }
        this.setState({ isSaving: false, error: response?.message || 'Could not save' });
    }

    render() {
        const { isNew } = this.props;
        const {
            qrPayload, brand, model, caliber, validUntil, note, scanning, decoding, decodeFailed, error, isSaving,
        } = this.state;

        return (
            <div class='w-100 d-flex justify-content-center'>
                <div class='w-100' style='max-width: 720px; padding-top: 20px; padding-bottom: 100px;'>
                    <div class='px-3'>
                        <h5 class='mb-3'>
                            <i class='fa-duotone fa-id-card me-2' />
                            {isNew
                                ? <Text id='weaponCard.add'>Add weapon card</Text>
                                : <Text id='weaponCard.edit-title'>Edit weapon card</Text>}
                        </h5>

                        <div class='card mb-3'>
                            <div class='card-body'>
                                <h6 class='card-title'><Text id='weaponCard.capture-title'>The QR code from your card</Text></h6>
                                <small class='text-body-secondary d-block mb-2'>
                                    <Text id='weaponCard.capture-help'>Scan the QR code with the camera or pick the image you downloaded from Altinn. The image is read on your phone and never uploaded.</Text>
                                </small>
                                {scanning ? (
                                    <div>
                                        <Camera scanQRCodes={true} scanBarcodes={false} showCapture={false} showCameraToggle={true} followUrls={false} callback={this.onScan} closeCamera={this.stopScan} />
                                        <button type='button' class='btn btn-outline-secondary rounded-pill mt-2' onClick={this.stopScan}>
                                            <Text id='weaponCard.stop-scan'>Stop scanning</Text>
                                        </button>
                                    </div>
                                ) : (
                                    <div class='d-flex flex-wrap gap-2 mb-2'>
                                        <button type='button' class='btn btn-outline-primary rounded-pill btn-scan' onClick={this.startScan}>
                                            <i class='fa-solid fa-camera me-1' />
                                            <Text id='weaponCard.scan'>Scan with camera</Text>
                                        </button>
                                        <label class='btn btn-outline-primary rounded-pill mb-0'>
                                            <i class='fa-solid fa-image me-1' />
                                            <Text id='weaponCard.pick-image'>Pick image</Text>
                                            <input type='file' accept='image/*' class='d-none' name='cardImage' onChange={this.onFile} />
                                        </label>
                                        {decoding && <span class='spinner-border spinner-border-sm align-self-center' />}
                                    </div>
                                )}
                                {decodeFailed && (
                                    <div class='alert alert-warning py-2'>
                                        <Text id='weaponCard.decode-failed'>No QR code was found in that image. Try a sharper photo or paste the text below.</Text>
                                    </div>
                                )}
                                <label class='form-label mt-2'><Text id='weaponCard.payload'>QR code text</Text></label>
                                <textarea class='form-control font-monospace' name='qrPayload' rows='3' value={qrPayload} onInput={this.onField} />
                                {qrPayload.trim().length > 0 && (
                                    <small class='text-success d-block mt-1'>
                                        <i class='fa-solid fa-check me-1' />
                                        <Text id='weaponCard.payload-ok'>QR code text captured</Text>: {qrPayload.trim().slice(0, 60)}
                                    </small>
                                )}
                            </div>
                        </div>

                        <div class='row'>
                            <div class='col-6 mb-3'>
                                <label class='form-label'><Text id='weaponCard.brand'>Brand</Text></label>
                                <input class='form-control' type='text' name='brand' value={brand} onInput={this.onField} />
                            </div>
                            <div class='col-6 mb-3'>
                                <label class='form-label'><Text id='weaponCard.model'>Model</Text></label>
                                <input class='form-control' type='text' name='model' value={model} onInput={this.onField} />
                            </div>
                            <div class='col-6 mb-3'>
                                <label class='form-label'><Text id='weaponCard.caliber'>Caliber</Text></label>
                                <input class='form-control' type='text' name='caliber' value={caliber} onInput={this.onField} />
                            </div>
                            <div class='col-6 mb-3'>
                                <label class='form-label'><Text id='weaponCard.valid-until'>Valid until</Text></label>
                                <input class='form-control' type='date' name='validUntil' value={validUntil} onInput={this.onField} />
                            </div>
                        </div>
                        <div class='mb-3'>
                            <label class='form-label'><Text id='weaponCard.note'>Note</Text></label>
                            <input class='form-control' type='text' name='note' value={note} onInput={this.onField} />
                        </div>

                        {error && <div class='alert alert-danger py-2'>{error}</div>}

                        <div class='d-flex justify-content-end'>
                            <button type='button' class='btn btn-primary rounded-pill' disabled={!this.canSave()} onClick={this.save}>
                                {isSaving && <span class='spinner-border spinner-border-sm me-2' />}
                                <i class='fa-solid fa-save me-1' />
                                <Text id='weaponCard.save'>Save</Text>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

export default WeaponCardDrawer;
